import React, {useMemo, useState} from "react";
import {Card, CardBody, CardHeader, CardTitle, Row, Col, Button, ButtonGroup} from "reactstrap";
import {BackgroundColorContext} from "../../contexts/BackgroundColorContext";
import {ChartThemeProvider, useChartTheme} from "../../contexts/ChartThemeContext";
import FpsReport from "./FpsReport";
import DashboardReport from "./Dashboard/DashboardReport";
import UdpReport from "./UdpReport";
import GenericValueBarChartReport from "./GenericValueBarChartReport";
import LatencyReport from "./LatencyReport";
import WorldClockOffsetsReport from "./WorldClockOffsetsReport";
import AntRxFailuresReport from "./AntRxFailuresReport";
import AntRxFailuresEmptyReport from "./AntRxFailuresEmptyReport";
import GroupEventDetails from "./GroupEventDetails";
import colors from "../../styles/colors";
import {ChartErrorBoundary} from "../Charts/ChartErrorBoundary";
import {createChartDataProcessor} from "../../utils/chartDataProcessor";
import {xaxisToTimeline, DataSeries, dataSeriesToPoints, dataSeriesLength} from "../../utils/dataAlignment";
import {generateConsistentTicks} from "../../utils/chartTicks";
import {
  CHART_RENDERING_CONFIG,
  ACTIVE_TIMELINE_CONFIG,
  PERFORMANCE_CHART_CONFIG,
  EVENT_CHART_CONFIG
} from "../../configs/chartPerformance";
import {
  withChartMemoization,
  memoizedGenericChartPropsAreEqual,
  memoizedPerformanceChartPropsAreEqual
} from "../Charts/MemoizedChartWrapper";
import ReactGA from "react-ga4";
import {GoogleAnalyticsEventCategories} from "../../constants";
import {normalizeBleDisconnects} from "../../utils/bleDisconnectsTransform";
import {localizeTimestamp, localizeDataPoints} from "../../utils/functions";

interface ChartsReportProps {
  world: string;
  humanizedDuration: string;
  version?: string;
  xaxis: DataSeries;
  session: {
    durationInSeconds: number;
    antDevices?: any[];
    bleDevicesNative?: any[];
    bleDevicesCompanion?: any[];
    bleDevicesLan?: any[];
  };
  groupEventDetails: any;
  system: any;
  fps: any;
  networkMetricsUdp: any;
  antRxFailures?: any[];
  latencyTestFailures: any;
  networkErrorsUdp: any;
  networkDisconnectsUdp: any;
  networkTimeoutsUdp: any;
  networkConnectionAttemptsUdp: any;
  networkDisconnectsTcp: any;
  networkTimeoutsTcp: any;
  networkErrorsTcp: any;
  bleDisconnects: any;
  bleFtmsResends: any;
  bleFtmsTrainerResets: any;
  antDisconnects: any;
  antFetTransferFails: any;
  companionAppConnectionAttempts: any;
  worldClockOffsets: any;
  activityOptions?: any[];
  selectedActivity?: number;
  onActivityChange?: (index: number) => void;
  // Advanced analysis props
  userId?: string;
  logId?: string;
  jwtToken?: string;
  analysisStatus?: import("../../types/advancedAnalysis").AnalysisStatus;
  onAnalysisStatusChange?: (status: import("../../types/advancedAnalysis").AnalysisStatus) => void;
  isPublic?: boolean;
  isDemo?: boolean;
  // Timezone localization
  timezoneOffsetMinutes?: number;
}

// Create memoized versions of chart components for better performance
const MemoizedGenericValueBarChartReport = CHART_RENDERING_CONFIG.enableMemoization
  ? withChartMemoization(GenericValueBarChartReport, memoizedGenericChartPropsAreEqual)
  : GenericValueBarChartReport;

const MemoizedFpsReport = CHART_RENDERING_CONFIG.enableMemoization
  ? withChartMemoization(FpsReport, memoizedPerformanceChartPropsAreEqual)
  : FpsReport;

const MemoizedUdpReport = CHART_RENDERING_CONFIG.enableMemoization
  ? withChartMemoization(UdpReport, memoizedPerformanceChartPropsAreEqual)
  : UdpReport;

const MemoizedAntRxFailuresReport = CHART_RENDERING_CONFIG.enableMemoization
  ? withChartMemoization(AntRxFailuresReport, memoizedGenericChartPropsAreEqual)
  : AntRxFailuresReport;

const MemoizedWorldClockOffsetsReport = CHART_RENDERING_CONFIG.enableMemoization
  ? withChartMemoization(WorldClockOffsetsReport, memoizedGenericChartPropsAreEqual)
  : WorldClockOffsetsReport;

const ChartsReportContent: React.FC<ChartsReportProps> = React.memo((props) => {
  performance.now();

  const {getThemeColors} = useChartTheme();
  const themeColors = getThemeColors();
  useMemo(() => createChartDataProcessor(), []);

  // View mode state - 3.0 vs 2.0 charts
  const [viewMode, setViewMode] = useState<"3" | "2">("3");

  // Handle v2.0 button click with analytics tracking
  const handleV2ButtonClick = () => {
    ReactGA.event({
      category: GoogleAnalyticsEventCategories.CHARTS,
      action: "switch to v2.0",
      label: `${props.world} - ${props.humanizedDuration}`
    });
    setViewMode("2");
  };

  if (!props || !props.world || !props.xaxis || dataSeriesLength(props.xaxis) === 0) {
    return <></>;
  }

  // Helper function to create timeline with specific sampling config
  const createTimelineWithSampling = useMemo(() => {
    performance.now();
    return (samplingConfig: typeof ACTIVE_TIMELINE_CONFIG) => {
      if (!props.xaxis || dataSeriesLength(props.xaxis) === 0) {
        return null;
      }

      const timezoneOffset = props.timezoneOffsetMinutes || 0;

      // Convert DataSeries to points array (handles both points and compressed formats)
      const xaxisPoints = dataSeriesToPoints(props.xaxis);

      // Localize timeline points to user's timezone
      let timelinePoints = xaxisPoints.map(
        ([timestamp, value]) => [localizeTimestamp(timestamp, timezoneOffset), value] as [number, number]
      );

      // Apply data sampling if configured
      if (
        samplingConfig.maxTimelinePoints &&
        samplingConfig.maxTimelinePoints > 0 &&
        samplingConfig.samplingStrategy !== "none" &&
        timelinePoints.length > samplingConfig.maxTimelinePoints
      ) {
        const originalLength = timelinePoints.length;

        if (samplingConfig.samplingStrategy === "uniform") {
          // Uniform sampling - take every nth point to reach target size
          const step = Math.floor(originalLength / samplingConfig.maxTimelinePoints);
          const sampledPoints = timelinePoints.filter((_, index) => index % step === 0);

          // Always include the last point to maintain timeline end (use localized version)
          const lastLocalizedPoint = timelinePoints[originalLength - 1];
          if (sampledPoints[sampledPoints.length - 1] !== lastLocalizedPoint) {
            sampledPoints.push(lastLocalizedPoint);
          }
          timelinePoints = sampledPoints;
        } else if (samplingConfig.samplingStrategy === "adaptive") {
          // Adaptive sampling - keep more points at the beginning and end
          const startPoints = Math.floor(samplingConfig.maxTimelinePoints * 0.2);
          const endPoints = Math.floor(samplingConfig.maxTimelinePoints * 0.2);
          const middlePoints = samplingConfig.maxTimelinePoints - startPoints - endPoints;

          const start = timelinePoints.slice(0, startPoints);
          const end = timelinePoints.slice(-endPoints);

          const middleStart = startPoints;
          const middleEnd = originalLength - endPoints;
          const middleStep = Math.floor((middleEnd - middleStart) / middlePoints);
          const middle = [];

          for (let i = middleStart; i < middleEnd; i += middleStep) {
            middle.push(timelinePoints[i]);
          }

          timelinePoints = [...start, ...middle, ...end];
        }
      }

      // Convert xaxis.points format to timeline format
      const timeline = xaxisToTimeline(timelinePoints);

      const timeRange = {
        start: timeline[0].timestamp,
        end: timeline[timeline.length - 1].timestamp
      };

      // Generate consistent ticks for all charts with timezone offset (0 since data is already localized)
      const ticks = generateConsistentTicks(timeRange, 20, 0);

      return {
        name: "xaxis",
        data: timeline,
        timeRange,
        ticks
      };
    };
  }, [props.xaxis, props.timezoneOffsetMinutes]);

  // Performance charts axis series (with sampling for speed)
  const performanceAxisSeries = useMemo(() => {
    return createTimelineWithSampling(PERFORMANCE_CHART_CONFIG);
  }, [createTimelineWithSampling]);

  // Event charts axis series (no sampling to preserve critical data)
  const eventAxisSeries = useMemo(() => {
    return createTimelineWithSampling(EVENT_CHART_CONFIG);
  }, [createTimelineWithSampling]);

  // Default axis series uses adaptive sampling
  const axisSeries = useMemo(() => {
    return createTimelineWithSampling(ACTIVE_TIMELINE_CONFIG);
  }, [createTimelineWithSampling]);

  // Detect activity switching for performance optimizations (currently unused but ready for future enhancements)
  // const isActivitySwitching = useActivitySwitchDetection(axisSeries);

  // Localize data points for v2.0 charts to match the localized axisSeries timeline
  const localizedFps = useMemo(() => {
    if (!props.fps?.points) {
      return props.fps;
    }
    return {
      ...props.fps,
      points: localizeDataPoints(props.fps.points, props.timezoneOffsetMinutes || 0)
    };
  }, [props.fps, props.timezoneOffsetMinutes]);

  const localizedNetworkMetricsUdp = useMemo(() => {
    if (!props.networkMetricsUdp?.points) {
      return props.networkMetricsUdp;
    }
    return {
      ...props.networkMetricsUdp,
      points: localizeDataPoints(props.networkMetricsUdp.points, props.timezoneOffsetMinutes || 0)
    };
  }, [props.networkMetricsUdp, props.timezoneOffsetMinutes]);

  const latencyTestFailuresProps: any = {
    ...props.latencyTestFailures,
    axisSeries,
    title: "Latency Test Failures",
    dataSeriesKey: "failures",
    seriesName: "failure",
    color: colors.fiestared
  };

  const udpNetworkErrorsProps: any = {
    ...props.networkErrorsUdp,
    axisSeries,
    title: "UDP Network Errors",
    color: colors.purple,
    dataSeriesKey: "errors",
    seriesName: "error",
    tooltipId: "networkErrorsUdp"
  };

  const udpNetworkDisconnectsProps: any = {
    ...props.networkDisconnectsUdp,
    axisSeries,
    title: "UDP Network Disconnects",
    color: colors.purple,
    dataSeriesKey: "errors",
    seriesName: "error",
    tooltipId: "udpNetworkDisconnects"
  };

  const networkTimeoutsUdpProps: any = {
    ...props.networkTimeoutsUdp,
    axisSeries,
    title: "UDP Network Timeouts",
    color: colors.yellow,
    dataSeriesKey: "timeouts",
    seriesName: "timeout",
    tooltipId: "networkTimeoutsUdp"
  };

  const networkConnectionAttemptsUdpProps: any = {
    ...props.networkConnectionAttemptsUdp,
    axisSeries,
    title: "Udp Network Connects",
    color: colors.lavender,
    dataSeriesKey: "connection",
    seriesName: "connection",
    tooltipId: "udpNetworkConnectionAttempts"
  };

  const tcpNetworkDisconnectsProps: any = {
    ...props.networkDisconnectsTcp,
    axisSeries,
    title: "TCP Network Disconnects",
    color: colors.fiestared,
    dataSeriesKey: "disconnects",
    seriesName: "disconnect",
    tooltipId: "tcpNetworkDisconnects"
  };

  const networkTimeoutsTcpProps: any = {
    ...props.networkTimeoutsTcp,
    axisSeries,
    title: "TCP Network Timeouts",
    color: colors.salmonpink,
    dataSeriesKey: "timeouts",
    seriesName: "timeout",
    tooltipId: "networkTimeoutsTcp"
  };

  const networkErrorsTcpProps: any = {
    ...props.networkErrorsTcp,
    axisSeries,
    title: "TCP Network Errors",
    color: colors.magenta,
    dataSeriesKey: "errors",
    seriesName: "error",
    tooltipId: "networkErrorsTcp"
  };

  // Normalize bluetooth disconnects data to handle both old and new formats
  const normalizedBleDisconnects = normalizeBleDisconnects(props.bleDisconnects, props.version);

  const bleDisconnectsProps: any = {
    ...normalizedBleDisconnects,
    axisSeries,
    title: "Bluetooth Disconnects",
    color: colors.tacxblue,
    dataSeriesKey: "disconnects",
    seriesName: "disconnect",
    tooltipId: "bluetoothDisconnects"
  };

  const bleFtmsResendsProps: any = {
    ...props.bleFtmsResends,
    axisSeries,
    title: "Bluetooth FTMS Message Resends",
    color: colors.sonicblue,
    dataSeriesKey: "value",
    seriesName: "resend",
    tooltipId: "bluetoothFtmsResends"
  };

  const bleFtmsTrainerResetsProps: any = {
    ...props.bleFtmsTrainerResets,
    axisSeries,
    title: "Bluetooth Trainer Resets",
    color: colors.lightblue,
    dataSeriesKey: "value",
    seriesName: "reset",
    tooltipId: "bluetoothTrainerResets"
  };

  const antDisconnectsProps: any = {
    ...props.antDisconnects,
    axisSeries: eventAxisSeries,
    title: "ANT+ Disconnects",
    color: colors.fiestared,
    dataSeriesKey: "disconnects",
    seriesName: "disconnect",
    tooltipId: "antDisconnects"
  };

  const antFetTransferFailuresProps: any = {
    ...props.antFetTransferFails,
    axisSeries: eventAxisSeries,
    title: "ANT+ FET Transfer Failures (Gradient/Resistance Change Failures)",
    color: colors.orange,
    dataSeriesKey: "fails",
    seriesName: "failure",
    tooltipId: "antFetTransferFailures",
    showrate: true,
    durationInSeconds: props.session.durationInSeconds
  };

  const companionAppConnectionAttemptsProps: any = {
    ...props.companionAppConnectionAttempts,
    axisSeries: eventAxisSeries,
    title:
      "Companion App Connection Attempts (Empty chart is normal when connection is made before starting activities)",
    color: colors.orange,
    dataSeriesKey: "value",
    seriesName: "connection attempt",
    tooltipId: "companionAppConnectionAttempts"
  };

  let antRxFailElements: any[] = [];

  const antRxFailColors: string[] = [colors.sonicblue, colors.beige, colors.seafoamgreen, colors.salmonpink];

  // Check for ANT+ devices - either from session data or by presence of ANT+ related data
  const hasAntDevices = !!(
    (props.session?.antDevices && props.session.antDevices.length > 0) ||
    (props.antRxFailures && props.antRxFailures.length > 0) ||
    (props.antDisconnects && (props.antDisconnects.points?.length > 0 || props.antDisconnects.length > 0))
  );

  if (hasAntDevices) {
    // Always create 4 charts when ANT+ devices are present

    // Create a map of channel to rxFailure data
    const channelDataMap: {[key: number]: any} = {};
    if (props.antRxFailures && props.antRxFailures.length > 0) {
      props.antRxFailures.forEach((rxFail, index) => {
        // Try to extract channel from the data
        let channel = rxFail.channel;

        // If channel is not set, try to extract from label
        if (!channel && rxFail.label) {
          const channelMatch = rxFail.label.match(/Channel (\d+)/);
          if (channelMatch) {
            channel = parseInt(channelMatch[1]);
          }
        }

        // If still no channel, use the array index + 1
        if (!channel) {
          channel = index + 1;
        }

        if (channel >= 1 && channel <= 4) {
          channelDataMap[channel - 1] = rxFail; // Convert to 0-based index
        }
      });
    }

    // Create charts for all 4 channels
    for (let i = 0; i < 4; i++) {
      if (channelDataMap[i]) {
        // Use actual data if available for this channel
        const antRxFailuresProps = {
          ...channelDataMap[i],
          title: `ANT+ Interference ${channelDataMap[i].label}`,
          axisSeries: eventAxisSeries,
          color: antRxFailColors[i]
        };
        antRxFailElements.push(<MemoizedAntRxFailuresReport {...antRxFailuresProps} key={`ant-${i}`} />);
      } else {
        // Create empty chart for unused channel
        // Match the structure of actual data but with empty/zero values
        // Create minimal points to ensure chart renders
        const startTime = eventAxisSeries?.timeRange?.start || Date.now() - 3600000; // 1 hour ago if no range
        const endTime = eventAxisSeries?.timeRange?.end || Date.now();
        const emptyPoints: Array<[number, number]> = [
          [startTime, 0],
          [endTime, 0]
        ]; // Two points to establish range

        const emptyAntRxFailuresProps = {
          channel: i + 1,
          label: `Channel ${i + 1} - None`,
          points: emptyPoints, // Minimal points to establish chart
          failureRate: 0,
          percentage: 0, // Include both percentage and failureRate
          title: `ANT+ Interference Channel ${i + 1} - None`,
          axisSeries: eventAxisSeries || undefined,
          color: antRxFailColors[i]
        };
        // Use non-memoized version for empty charts to avoid any memoization issues
        antRxFailElements.push(<AntRxFailuresReport {...emptyAntRxFailuresProps} key={`ant-${i}`} />);
      }
    }
  } else {
    // No ANT+ devices and no data - show single empty report
    antRxFailElements.push(<AntRxFailuresEmptyReport axisSeries={axisSeries} key={"ant-0"} />);
  }

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <ChartErrorBoundary>
          <Row>
            <Col md="12">
              <Card>
                <CardHeader data-color={color}>
                  <Row>
                    <Col md="8">
                      <CardTitle tag="h4">
                        {props.activityOptions && props.activityOptions.length > 1 ? (
                          <select
                            className="activity-selector"
                            value={props.selectedActivity}
                            onChange={(e) => props.onActivityChange && props.onActivityChange(parseInt(e.target.value))}
                          >
                            {props.activityOptions.map((option: any, index: number) => (
                              <option key={index} value={index}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <>
                            {props.world} <span className="sub-heading">{props.humanizedDuration}</span>
                          </>
                        )}
                      </CardTitle>
                    </Col>
                    <Col md="4" className="text-right">
                      <ButtonGroup>
                        <Button
                          color="primary"
                          data-color={color}
                          size="sm"
                          outline={viewMode !== "3"}
                          active={viewMode === "3"}
                          onClick={() => setViewMode("3")}
                        >
                          v3.0
                        </Button>
                        <Button
                          color="primary"
                          data-color={color}
                          size="sm"
                          outline={viewMode !== "2"}
                          active={viewMode === "2"}
                          onClick={handleV2ButtonClick}
                        >
                          v2.0
                        </Button>
                      </ButtonGroup>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {/* 3 View */}
                  {viewMode === "3" && (
                    <DashboardReport
                      world={props.world}
                      xaxis={props.xaxis}
                      fps={props.fps}
                      udpNetworkPackets={props.networkMetricsUdp}
                      networkDisconnectsTcp={props.networkDisconnectsTcp}
                      networkTimeoutsTcp={props.networkTimeoutsTcp}
                      networkErrorsTcp={props.networkErrorsTcp}
                      bleDisconnects={props.bleDisconnects}
                      antDisconnects={props.antDisconnects}
                      antRxFailures={props.antRxFailures}
                      antFetTransferFails={props.antFetTransferFails}
                      bleFtmsResends={props.bleFtmsResends}
                      bleFtmsTrainerResets={props.bleFtmsTrainerResets}
                      networkTimeoutsUdp={props.networkTimeoutsUdp}
                      networkErrorsUdp={props.networkErrorsUdp}
                      networkDisconnectsUdp={props.networkDisconnectsUdp}
                      networkConnectionAttemptsUdp={props.networkConnectionAttemptsUdp}
                      latencyTestFailures={props.latencyTestFailures}
                      worldClockOffsets={props.worldClockOffsets}
                      companionAppConnectionAttempts={props.companionAppConnectionAttempts}
                      system={props.system}
                      session={props.session}
                      groupEventDetails={props.groupEventDetails}
                      userId={props.userId}
                      logId={props.logId}
                      jwtToken={props.jwtToken}
                      analysisStatus={props.analysisStatus}
                      onAnalysisStatusChange={props.onAnalysisStatusChange}
                      isPublic={props.isPublic}
                      isDemo={props.isDemo}
                      timezoneOffsetMinutes={props.timezoneOffsetMinutes}
                    />
                  )}

                  {/* 2 Charts View */}
                  {viewMode === "2" && (
                    <>
                      {/* Group Event Details */}
                      {props.groupEventDetails && (
                        <div style={{marginBottom: "30px"}}>
                          <GroupEventDetails groupEventDetails={props.groupEventDetails} />
                        </div>
                      )}

                      {/* Performance & Network Correlation - FPS and UDP are inextricably linked */}
                      <div style={{marginTop: "30px"}}>
                        <h5
                          style={{
                            marginBottom: "20px",
                            paddingBottom: "10px",
                            borderBottom: `1px solid ${themeColors.border}`
                          }}
                        >
                          Performance & Network Correlation
                        </h5>
                        <MemoizedFpsReport {...localizedFps} {...props.system} axisSeries={performanceAxisSeries} />
                        <MemoizedUdpReport {...localizedNetworkMetricsUdp} axisSeries={performanceAxisSeries} />
                      </div>

                      {/* ANT+ Communication Analysis */}
                      <div style={{marginTop: "30px"}}>
                        <h5
                          style={{
                            marginBottom: "20px",
                            paddingBottom: "10px",
                            borderBottom: `1px solid ${themeColors.border}`
                          }}
                        >
                          ANT+ Communication Analysis
                        </h5>
                        {/* ANT+ Interference for channels 1-4 */}
                        {antRxFailElements.map((element, index) => (
                          <div key={`ant-interference-${index}`} style={{marginBottom: "15px"}}>
                            {element}
                          </div>
                        ))}
                        {/* ANT+ FET Transfer Failures */}
                        <MemoizedGenericValueBarChartReport key="ant-fet" {...antFetTransferFailuresProps} />
                        {/* ANT+ Disconnects */}
                        <MemoizedGenericValueBarChartReport key="ant-disconnects" {...antDisconnectsProps} />
                      </div>

                      {/* BLE Communication Analysis */}
                      <div style={{marginTop: "30px"}}>
                        <h5
                          style={{
                            marginBottom: "20px",
                            paddingBottom: "10px",
                            borderBottom: `1px solid ${themeColors.border}`
                          }}
                        >
                          Bluetooth Low Energy (BLE) Communication Analysis
                        </h5>
                        <MemoizedGenericValueBarChartReport key="ble-disconnects" {...bleDisconnectsProps} />
                        <MemoizedGenericValueBarChartReport key="ble-ftms-resends" {...bleFtmsResendsProps} />
                        <MemoizedGenericValueBarChartReport key="ble-trainer-resets" {...bleFtmsTrainerResetsProps} />
                      </div>

                      {/* Network Protocol Analysis */}
                      <div style={{marginTop: "30px"}}>
                        <h5
                          style={{
                            marginBottom: "20px",
                            paddingBottom: "10px",
                            borderBottom: `1px solid ${themeColors.border}`
                          }}
                        >
                          Network Protocol Analysis
                        </h5>
                        <div>
                          <h6 style={{color: themeColors.primary, marginBottom: "15px"}}>UDP Communications</h6>
                          <MemoizedGenericValueBarChartReport key="udp-timeouts" {...networkTimeoutsUdpProps} />
                          <MemoizedGenericValueBarChartReport key="udp-errors" {...udpNetworkErrorsProps} />
                          <MemoizedGenericValueBarChartReport key="udp-disconnects" {...udpNetworkDisconnectsProps} />
                          <MemoizedGenericValueBarChartReport
                            key="udp-connections"
                            {...networkConnectionAttemptsUdpProps}
                            title="UDP Network Connects (Many entries here is normal)"
                          />
                        </div>
                        <div style={{marginTop: "20px"}}>
                          <h6 style={{color: themeColors.secondary, marginBottom: "15px"}}>TCP Communications</h6>
                          <MemoizedGenericValueBarChartReport key="tcp-timeouts" {...networkTimeoutsTcpProps} />
                          <MemoizedGenericValueBarChartReport key="tcp-errors" {...networkErrorsTcpProps} />
                          <MemoizedGenericValueBarChartReport key="tcp-disconnects" {...tcpNetworkDisconnectsProps} />
                        </div>
                      </div>

                      {/* Network Diagnostics */}
                      <div style={{marginTop: "30px"}}>
                        <h5
                          style={{
                            marginBottom: "20px",
                            paddingBottom: "10px",
                            borderBottom: `1px solid ${themeColors.border}`
                          }}
                        >
                          Network Diagnostics
                        </h5>
                        <div>
                          <LatencyReport key="latency" {...latencyTestFailuresProps} />
                          <MemoizedWorldClockOffsetsReport
                            key="clock-offsets"
                            {...props.worldClockOffsets}
                            axisSeries={axisSeries}
                          />
                        </div>
                        <div style={{marginTop: "20px"}}>
                          <MemoizedGenericValueBarChartReport
                            key="companion"
                            {...companionAppConnectionAttemptsProps}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </ChartErrorBoundary>
      )}
    </BackgroundColorContext.Consumer>
  );
});

const ChartsReport: React.FC<ChartsReportProps> = (props) => {
  return (
    <ChartThemeProvider>
      <ChartsReportContent {...props} />
    </ChartThemeProvider>
  );
};

export default ChartsReport;
